import { ComponentType, FC, HTMLProps, SVGAttributes } from 'react';
import clsx from 'clsx';
import { isAfter, isBefore } from 'date-fns';

import DefectBeperktInzetbaarXs from '@/assets/icons/DefectBeperktInzetbaarXs';
import DefectInzetbaarXs from '@/assets/icons/DefectInzetbaarXs';
import DefectNietInzetbaarXs from '@/assets/icons/DefectNietInzetbaarXs';
import DefectZandloperXs from '@/assets/icons/DefectZandloperXs';
import UserXs from '@/assets/icons/UserXs';
import { Defect } from '@/types/Defecten';
import { dateWithDayOfWeekWithTime, dateWithDayOfWeekWithYearWithTime, endOfTomorrow2AM } from '@/utils/dateUtils';

import styles from './DefectRow.module.css';

const defectIconMap: Record<string, ComponentType<SVGAttributes<SVGSVGElement>>> = {
  DefectNietInzetbaarXs,
  DefectBeperktInzetbaarXs,
  DefectInzetbaarXs,
  DefectZandloperXs,
};

export type DefectPositie = 'enkelvoudig' | 'eerste' | 'laatste';

export const defectSoortIcon = (defectSoort: string, nietMeerInzetbaarVanaf: number | null, nu: Date) => {
  const icon = nietInzetbaarOfZandloper(nietMeerInzetbaarVanaf, nu);

  if (icon) {
    return icon;
  }

  switch (defectSoort) {
    case 'DEFECT_NIET_INZETBAAR':
      return 'DefectNietInzetbaarXs'; // blauw
    case 'BEPERKING_IN_TREINDIENST':
      return 'DefectBeperktInzetbaarXs'; // rood
    case 'GEEN_BEPERKINGEN':
      return 'DefectInzetbaarXs'; // grijs
  }
  return 'DefectInzetbaarXs';
};

export const nietInzetbaarOfZandloper = (nietMeerInzetbaarVanaf: number | null, nu: Date) => {
  const lastDayOfViewAt2AM = endOfTomorrow2AM(nu);
  // Wanneer defect niet inzetbaar in bijsturing-periode, tonen zandloper
  if (
    nietMeerInzetbaarVanaf &&
    isAfter(nietMeerInzetbaarVanaf, nu) &&
    isBefore(nietMeerInzetbaarVanaf, lastDayOfViewAt2AM)
  ) {
    return 'DefectZandloperXs';
  }
  if (nietMeerInzetbaarVanaf && isBefore(nietMeerInzetbaarVanaf, nu)) {
    return 'DefectNietInzetbaarXs';
  }

  return null;
};

export const defectSoortTooltip = (defectSoort: string) => {
  switch (defectSoort) {
    case 'DEFECT_NIET_INZETBAAR':
      return 'Defect, niet inzetbaar'; // blauw
    case 'BEPERKING_IN_TREINDIENST':
      return 'Defect, beperkt inzetbaar'; // rood
    case 'GEEN_BEPERKINGEN':
      return 'Defect, inzetbaar'; // grijs
  }
  return 'Defect';
};

export type DefectProps = {
  defect: Defect;
  positie: DefectPositie;
  disabledHoverAndBorder?: boolean;
  showBorderBottom?: boolean;
  showEenheidNummer?: boolean;
  showYearDate?: boolean;
  toonIconNietInzetbaar?: boolean;
  noPadding?: boolean;
  stackedLayout?: boolean;
} & HTMLProps<HTMLDivElement>;

const DefectRow: FC<DefectProps> = ({
  defect,
  positie = 'enkelvoudig',
  disabledHoverAndBorder = false,
  showBorderBottom = false,
  showEenheidNummer = true,
  showYearDate = true,
  toonIconNietInzetbaar = false,
  noPadding = false,
  stackedLayout = false,
  ...rest
}) => {
  const { style, ...divProps } = rest;
  const iconDefect = defectSoortIcon(defect.soort, null, new Date());
  const iconNietInzetbaar = nietInzetbaarOfZandloper(
    defect.nietMeerInzetbaarVanaf ? new Date(defect.nietMeerInzetbaarVanaf).getTime() : null,
    new Date(),
  );

  const dateContent =
    positie === 'laatste' ? (
      <span className={styles.timeSpanStyle}>
        Niet inzetbaar vanaf: {dateWithDayOfWeekWithYearWithTime(defect.nietMeerInzetbaarVanaf)}
      </span>
    ) : (
      <span className={styles.timeSpanStyle}>
        {showYearDate
          ? dateWithDayOfWeekWithYearWithTime(defect.beginTijd)
          : dateWithDayOfWeekWithTime(defect.beginTijd)}
      </span>
    );

  const leadingIcons = (variant: 'stacked' | 'default') => {
    const mainIconName =
      positie === 'laatste'
        ? defectSoortIcon(
            defect.soort,
            defect.nietMeerInzetbaarVanaf ? new Date(defect.nietMeerInzetbaarVanaf).getTime() : null,
            new Date(),
          )
        : iconDefect;
    const MainIcon = defectIconMap[mainIconName];
    const NietInzetbaarIcon = iconNietInzetbaar ? defectIconMap[iconNietInzetbaar] : null;

    return (
      <>
        {MainIcon && (
          <MainIcon
            className={clsx(
              styles.defectIconStyle,
              styles[defect.soort as 'BEPERKING_IN_TREINDIENST' | 'DEFECT_NIET_INZETBAAR' | 'GEEN_BEPERKINGEN'],
            )}
            aria-hidden='true'
            width='20px'
            height='20px'
          />
        )}
        {toonIconNietInzetbaar && iconNietInzetbaar && iconNietInzetbaar != iconDefect && NietInzetbaarIcon ? (
          <NietInzetbaarIcon
            className={[styles.defectIconStyleXs, styles[`DEFECT_NIET_INZETBAAR`]].join(' ')}
            aria-hidden='true'
            width='18px'
            height='18px'
          />
        ) : null}
        {defect.bron === 'HANDMATIG' && (
          <UserXs height='20px' width='20px' aria-label='User' className={styles.userIconStyle} />
        )}
        {variant === 'stacked' && defect.werkplaats && (
          <span className={styles.werkplaatsSpanStackedStyle} title={defect.werkplaats}>
            {defect.werkplaats.toLowerCase()}
          </span>
        )}
        {variant === 'stacked' && showEenheidNummer && (
          <span className={styles.eenheidSpanStyle} data-testid={`defect-werkorder-${defect.werkorderId}`}>
            {defect.eenheidNummer}
          </span>
        )}
        {variant === 'default' && showEenheidNummer && (
          <span className={styles.eenheidSpanStyle} data-testid={`defect-werkorder-${defect.werkorderId}`}>
            {defect.eenheidNummer}
          </span>
        )}
      </>
    );
  };

  if (stackedLayout && ['enkelvoudig', 'eerste'].includes(positie)) {
    return (
      <div
        style={noPadding ? { ...style, padding: 0 } : style}
        className={clsx(
          styles.defectRow,
          styles.stackedRow,
          !disabledHoverAndBorder && styles.defectRowWithHoverAndBorder,
          styles.defectenRowIdentifiersStyle,
          showBorderBottom && styles.defectRowWithBorderBottom,
        )}
        data-positie={positie}
        data-testid={`defect_${defect.eenheidNummer}_${positie}`}
        data-greyed-out={defect.gereedgemeld ? 'yes' : 'no'}
        {...divProps}
      >
        <div className={styles.stackedHeader}>
          <div className={styles.stackedHeaderLeft}>{leadingIcons('stacked')}</div>
          <div className={styles.stackedHeaderRight}>{dateContent}</div>
        </div>
        <div
          className={styles.stackedToelichting}
          style={defect.bron === 'HANDMATIG' ? { fontStyle: 'italic' } : undefined}
          title={defect.toelichting}
        >
          {defect.toelichting}
        </div>
      </div>
    );
  }

  return (
    <div
      style={noPadding ? { ...style, padding: 0 } : style}
      className={clsx(
        styles.defectRow,
        !disabledHoverAndBorder && styles.defectRowWithHoverAndBorder,
        styles.defectenRowIdentifiersStyle,
        showBorderBottom && styles.defectRowWithBorderBottom,
      )}
      data-positie={positie}
      data-testid={`defect_${defect.eenheidNummer}_${positie}`}
      data-greyed-out={defect.gereedgemeld ? 'yes' : 'no'}
      {...divProps}
    >
      {leadingIcons('default')}
      {['enkelvoudig', 'eerste'].includes(positie) && (
        <>
          <span
            className={styles.toelichting}
            style={defect.bron === 'HANDMATIG' ? { fontStyle: 'italic' } : undefined}
            title={defect.toelichting}
          >
            {defect.toelichting}
          </span>

          {defect.werkplaats && (
            <span className={styles.werkplaatsSpanStyle} title={defect.werkplaats}>
              {defect.werkplaats.toLowerCase()}
            </span>
          )}

          <div className={styles.timeDivStyle}>{dateContent}</div>
        </>
      )}

      {positie === 'laatste' && <div className={styles.timeInzetbaarDivStyle}>{dateContent}</div>}
    </div>
  );
};

export default DefectRow;
